import {
  INetHuntFeedbackMessageConfiguration,
  INetHuntFunnel,
} from 'src/components/profileComponents/Integrations/Nethunt/types';
import { IAdminPanel, IFrontConfigBase } from 'src/store/type-displayed-spinner/types';
import { DeveloperPropertyType } from 'src/components/profileComponents/Developers/components/types';
import { MarketplaceTilesMinMaxRangeType } from 'src/typings/marketplace';
import { IApplicationProperty, IPlacementProperty, ITranslate } from 'src/typings/viewConfig';
import { PlacementShowModule } from 'src/typings/filter';
import { ComplexStatus } from 'src/constants/complexStatus';
import { IUsers } from './user';
import { IDField, IDogeFields, IDogeFieldsOptional } from './global-config';

export enum IGtmEventsTypes {
  formSubmit = 'FormSubmitButtonClick',
  pdfDownload = 'DownloadPDFButtonClick',
}
export type IStage = IBitrixStage | ICreatioStage | INetHuntStages;

export interface IBitrixStage extends IDogeFields {
  bitrixCategory: string;
  bitrixDeals: string[];
  bitrixExternalStageId: string;
  bitrixExternalStageName: string;
  id: number;
  priority: number;
  placementStatuses: string[];
}

export interface ICreatioStage extends Omit<IDogeFields, '@context'> {
  id: number;
  creatioProject: string;
  externalId: string;
  externalName: string;
  priority: number;
  sortSequence: number;
  unlinkPlacementAtStageWithLowerPriority: boolean;
  unlinkPlacementAtStageWithHigherPriority: boolean;
  unlinkPlacementAtStageWithSamePriority: boolean;
  unlinkAllPlacementsAtAllOtherStages: boolean;
  unlinkPlacementAtThisStage: boolean;
  blockMoveAndLinkDealsWithPlacementAtOtherStages: boolean;
  blockMoveNdLinkDealsWithPlacementAtOrAftrCurrentStage: boolean;
  placementStatuses: string[];
}

export interface INetHuntStages extends IDogeFieldsOptional {
  externalName: string,
  funnel: string,
  placementStatuses: string[],
  priority: number,
  sortOrder: number,
  unlinkPlacementAtStageWithLowerPriority: boolean,
  unlinkPlacementAtStageWithHigherPriority: boolean,
  unlinkPlacementAtStageWithSamePriority: boolean,
  unlinkAllPlacementsAtAllOtherStages: boolean,
  unlinkPlacementAtThisStage: boolean,
  blockMoveAndLinkDealsWithPlacementAtOtherStages: boolean,
  blockMoveNdLinkDealsWithPlacementAtOrAftrCurrentStage: boolean
}

export interface IStatusOnZeroDeals extends IDogeFields {
  id: number,
  complex: IComplex,
  placementStatus: IStatus
}

export interface ICategory extends IDogeFields {
  bitrixCategoryPlacementStatusOnZeroDeals: IStatusOnZeroDeals[];
  bitrixExternalCategoryName: string;
  bitrixExternalCategoryId: string;
  complexes: IComplex[];
  bitrixStages: string[];
  id: number;
}

export interface ICategoryChild extends IDogeFields {
  bitrixExternalCategoryName: string;
  bitrixExternalCategoryId: string;
  complexes: string[];
  bitrixStages: string[];
}

export interface IProjectFrontDealConfig extends IDogeFields {
  id: number;
  isShowDealDataForManagers: boolean;
}

export interface IProjectFrontConfig extends IDogeFields {
  id: number;
  bitrixProject: string;
  dealConfig: IProjectFrontDealConfig;
}

export interface IProject extends IDogeFields {
  id: number | string;
  domain: string;
  webhookUrl: string;
  workspaceId: string;
  authKey: string;
  authEmail: string;
  formEndpoint: string;
  funnels: INetHuntFunnel[] | [],
  formMessageConfiguration: INetHuntFeedbackMessageConfiguration | null;
  developer: string;
  bitrixCategories: ICategoryChild[];
  login: string;
  password: string;
  oauth2Enpoint: string;
  oauth2ClientId: string;
  oauth2ClientSecret: string;
  creatioStages: IStage[];
  serverIp: string;
  leadEndpointSuffix: string;
  leadSchemaName: 'Lead';
  leadSchemaUid: string;
  opportunityEndpointSuffix: string;
  opportunitySchemaName: 'Opportunity';
  opportunitySchemaUid: string;
  opportunityStageEndpointSuffix: string;
  opportunityStageSchemaName: 'OpportunityStage';
  frontConfig: IProjectFrontConfig;
}

export interface ISpecificationPropBase extends IDogeFields {
  name: {[key: string]: string};
  sortOrder: number;
  type: DeveloperPropertyType,
  prefix: {[key: string]: string},
  suffix: {[key: string]: string},
}

export type ISpecificationProp =
  | (ISpecificationPropBase & { id: number; tempId?: never })
  | (ISpecificationPropBase & { tempId: number; id?: never });

export interface IDevelopmentSpecification extends IDogeFieldsOptional {
  id: number,
  type?: 'ComplexDevelopmentSpecification';
  purchaseTerms: ISpecificationProp[] | [],
  propertyClasses: ISpecificationProp[] | [],
  salesStatuses: ISpecificationProp[] | [],
  constructionPhases: ISpecificationProp[] | [],
  completionDates: ISpecificationProp[] | [],
}

export interface IComplexLocation extends IDogeFields, IDField {
  latitude: string | null;
  longitude: string | null;
  countryManual: {[key: string]: string} | null;
  cityManual: {[key: string]: string} | null;
  districtManual: {[key: string]: string} | null;
  nearestMetroStationManual: {[key: string]: string} | null;
  nearestMetroBranchManual: {[key: string]: string} | null;
  nearestMetroWalkMinutesManual: {[key: string]: string} | null;
  nearestMetroDriveMinutesManual: {[key: string]: string} | null;
  countryAutoCalculated: {[key: string]: string} | null;
  cityAutoCalculated: {[key: string]: string} | null;
  districtAutoCalculated: {[key: string]: string} | null;
  nearestMetroStationAutoCalculated: {[key: string]: string} | null;
  nearestMetroBranchAutoCalculated: {[key: string]: string} | null;
  nearestMetroWalkMinutesAutoCalculated: {[key: string]: string} | null;
  nearestMetroDriveMinutesAutoCalculated: {[key: string]: string} | null;
}

export interface IComplexDynamicMinMaxGroupingMemberSubValue extends IDogeFields, IDField {
  diType: MarketplaceTilesMinMaxRangeType.MIN,
  systemName: string;
  value: string;
}

export interface IComplexDynamicMinMaxGroupingMember {
  value: ITranslate | null
  subValues: IComplexDynamicMinMaxGroupingMemberSubValue[]
}

export interface IComplexDynamicMinMax extends IDogeFields, IDField {
  diType: MarketplaceTilesMinMaxRangeType.MINMAX,
  minValue: number,
  maxValue: number;
  systemName: string;
}

export interface IComplexDynamicMinMaxGrouping extends IDogeFields, IDField {
  diType: MarketplaceTilesMinMaxRangeType.GROUPINGMINMAX,
  members: IComplexDynamicMinMaxGroupingMember[],
  systemName: string;
}

export type IComplexDynamicProperties = IComplexDynamicMinMax | IComplexDynamicMinMaxGrouping;

export interface IDeveloper extends IDogeFields {
  id: number;
  logoFileUri: IFileUri | null;
  name: { [key: string]: string };
  language: string;
  secondaryLanguages: string[] | null;
  bitrixProject: string | null;
  creatioProject: string | null;
  netHuntIntegration: string | null;
  complexes: IComplex[];
  developmentSpecification: IDevelopmentSpecification
}

export interface IAmoPipeline extends IDogeFields {
  amoProject: string | null;
  amo_id: number;
  id: number;
}

export interface IGtmEvents extends IDogeFields, IDField {
  eventJson: string,
  type: IGtmEventsTypes,
}

export interface IGtm extends IDogeFields, IDField {
  events: IGtmEvents[],
}

export interface IComplexFrontConfigWebsite extends IFrontConfigBase {
  gtm: IGtm,
  isAiChatEnabled?: boolean,
}

export interface IComplexFrontConfig extends IDogeFields, IDField {
  adminPanel: IAdminPanel,
  website: IComplexFrontConfigWebsite,
}

export interface IComplex extends IDogeFields, Record<string, any> {
  amo_pipeline: IAmoPipeline | null;
  bitrixComplexData: null;
  developers: IDeveloper[];
  id: string;
  status: ComplexStatus;
  logoFileUri: IFileUri | null,
  previewFileUri: IFileUri | null,
  title: { [key: string]: string; },
  address: { [key: string]: string; } | null,
  secondaryLanguages: string[] | null,
  crosstableLanguage: string | null,
  crosstableSecondaryLanguages: string[] | null,
  moduleLanguage: string | null,
  moduleSecondaryLanguages: string[] | null,
  language: string;
  complexShortName: string;
  clientModuleAccessUrls: IComplexClientModuleAccessUrls[] | null,
  clientModuleConfig: IClientModuleConfig,
  showInCrosstable: boolean;
  users: IUsers[];
  usersCount?: number;
  placementsTotalCount?: number;
  supervisorsAndManagersCount?: number;
  frontConfig: IComplexFrontConfig;
  developmentSpecification: IDevelopmentSpecification | null;
  location: IComplexLocation
  dynamicProperties: IComplexDynamicProperties[]
  placementsAvailable: number;
  applicationProperties: IApplicationProperty[];
  placementProperties: IPlacementProperty[],
}

export interface IComplexClientModuleAccessUrls extends IDogeFields, IDField {
  url: string,
}

export interface IFileUri extends IDogeFields {
  id: number,
  domain: string,
  path: string,
  name: string,
  extension: string,
  resolutions: null,
  alternativeTypes: null,
  fileUrl: string,
}

export interface IClientModuleConfig extends IDogeFields, IDField {
  formDefaultCountry: string | null,
}

export enum TypeOfStatus {
  fixed = 'fixed time',
  static = 'static'
}

export interface IStatus extends IDogeFields {
  amoCanBeSetForDeal: boolean;
  amoOnlyOneDealPlacement: boolean;
  canBeChanged: boolean;
  canBeSet: boolean;
  color: string;
  crosstableColor: string | null;
  createdBy: string | null;
  complex: string;
  crosstableName: {
    [key: string]: string;
  };
  statusName: {
    [key: string]: string;
  };
  id: number;
  moduleName: {
    [key: string]: string;
  };
  name: {
    [key: string]: string;
  };
  showInModule: PlacementShowModule;
  temporaryType: TypeOfStatus|null;
  updateTimeInterval: number|null;
}

export interface IRequestParams {
  users: number | undefined
  properties: {[key: string]: string | string[]}
}
